<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import Modal from '$lib/components/ui/Modal.svelte';
  import { workLogsApi } from '$lib/api/workLogs';
  import { workZonesApi } from '$lib/api/workZones';
  import { getErrorMessage } from '$lib/api/client';
  import { activatePushForPunchIn, getPublicKey } from '$lib/api/push';
  import { leafletMap } from '$lib/utils/leafletMap';
  import { reverseGeocode, FALLBACK_NAME, FALLBACK_AREA } from '$lib/utils/mapsLink';
  import { workZoneMatch } from '$lib/utils/workZoneLocation';
  import { session } from '$lib/state/session.svelte';
  import { t } from '$lib/i18n';
  import { MapPin } from 'lucide-svelte';
  import type { PunchMode, WorkZone } from '$lib/types';

  const { onClose, onSuccess }: { onClose: () => void; onSuccess: (workLog: import('$lib/types').WorkLog, zoneName?: string) => void } = $props();

  const mode: PunchMode = $derived(session.me?.punch_mode ?? 'site');

  type GeoStatus = 'idle' | 'locating' | 'ok' | 'denied' | 'error';
  let geoStatus = $state<GeoStatus>('idle');
  let geoError = $state('');
  let lat = $state<number | null>(null);
  let lng = $state<number | null>(null);
  let accuracy = $state<number | null>(null);
  let placeName = $state('');
  let placeArea = $state('');
  let looking = $state(false);
  let pushKey = $state<string | null | undefined>(undefined);
  let capturedAt = $state<number | null>(null);
  let geoRequestID = 0;
  let geocodeRequestID = 0;
  let closed = false;
  let nameEdited = $state(false);
  let areaEdited = $state(false);
  let submissionCount = 0;
  const LOCATION_MAX_AGE_MS = 30_000;

  $effect(() => {
    getPublicKey().then((key) => (pushKey = key));
  });

  function clearLocation() {
    // Auto-filled details belong to the previous fix, not the next one.
    geocodeRequestID++;
    looking = false;
    if (!nameEdited) placeName = '';
    if (!areaEdited) placeArea = '';
    lat = null;
    lng = null;
    accuracy = null;
    capturedAt = null;
  }

  function requestLocation(): Promise<boolean> {
    const requestID = ++geoRequestID;
    clearLocation();
    geoStatus = 'locating';
    geoError = '';
    if (!navigator.geolocation) {
      geoStatus = 'error';
      geoError = t('punchIn.geoUnsupported');
      return Promise.resolve(false);
    }
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (closed || requestID !== geoRequestID) return resolve(false);
          lat = pos.coords.latitude;
          lng = pos.coords.longitude;
          accuracy = pos.coords.accuracy;
          capturedAt = Date.now();
          geoStatus = 'ok';
          resolve(true);
        },
        (err) => {
          if (closed || requestID !== geoRequestID) return resolve(false);
          clearLocation();
          geoStatus = err.code === err.PERMISSION_DENIED ? 'denied' : 'error';
          geoError =
            err.code === err.PERMISSION_DENIED
              ? t('punchIn.geoDenied')
              : t('punchIn.geoError', { msg: err.message });
          resolve(false);
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 12_000 }
      );
    });
  }

  let askedGeo = false;
  $effect(() => {
    if (askedGeo) return;
    askedGeo = true;
    void requestLocation();
  });

  $effect(() => {
    return () => {
      closed = true;
      geoRequestID++;
      geocodeRequestID++;
    };
  });

  const zonesQuery = createQuery(() => ({
    queryKey: ['my-work-zones'],
    queryFn: () => workZonesApi.mine(),
    enabled: mode !== 'untagged'
  }));
  const recentsQuery = createQuery(() => ({
    queryKey: ['recent-remote-locations'],
    queryFn: () => workLogsApi.recentRemoteLocations(),
    enabled: mode === 'field'
  }));
  const recents = $derived(recentsQuery.data?.locations ?? []);

  const zoneMatch = $derived.by(() => {
    if (lat === null || lng === null) return null;
    return workZoneMatch(zonesQuery.data ?? [], lat, lng);
  });

  const nearestZone = $derived(zoneMatch?.nearest ?? null);
  const containingZone = $derived(zoneMatch?.containing ?? null);
  const atSite = $derived(containingZone !== null);

  $effect(() => {
    if (mode !== 'field' || geoStatus !== 'ok' || lat === null || lng === null || atSite) return;
    let cancelled = false;
    const requestID = ++geocodeRequestID;
    looking = true;
    reverseGeocode(lat, lng).then((geo) => {
      if (cancelled || closed || requestID !== geocodeRequestID) return;
      looking = false;
      if (!nameEdited && geo.name && geo.name !== FALLBACK_NAME) placeName = geo.name;
      if (!areaEdited && geo.area && geo.area !== FALLBACK_AREA) placeArea = geo.area;
    });
    return () => {
      cancelled = true;
      geocodeRequestID++;
    };
  });

  let submitting = $state(false);
  let error = $state('');

  const canSubmit = $derived.by(() => {
    if (submitting || pushKey === undefined) return false;
    // Place details and reverse geocoding are optional; GPS is not.
    return geoStatus === 'ok' && lat !== null && lng !== null;
  });

  async function submit() {
    submitting = true;
    error = '';
    try {
      if (
        submissionCount > 0 ||
        capturedAt === null ||
        Date.now() - capturedAt > LOCATION_MAX_AGE_MS
      ) {
        if (!(await requestLocation())) return;
      }
      submissionCount++;
      const push = await activatePushForPunchIn(pushKey ?? null);
      if (!push.ok || (push.required && !push.endpoint)) {
        error = push.error ?? 'Push notifications must be enabled before punching in.';
        return;
      }
      let workLog;
      if (mode === 'untagged') {
        workLog = await workLogsApi.punchIn({
          lat: lat!,
          lng: lng!,
          accuracy: accuracy ?? undefined,
          push_endpoint: push.endpoint
        });
        onSuccess(workLog);
      } else if (mode === 'home' && !atSite) {
        workLog = await workLogsApi.punchIn({
          lat: lat!,
          lng: lng!,
          accuracy: accuracy ?? undefined,
          push_endpoint: push.endpoint
        });
        onSuccess(workLog, workLog.location_name || workLog.work_zone_name || t('punchIn.fallbackHome'));
      } else if (mode === 'field' && !atSite) {
        workLog = await workLogsApi.punchIn({
          lat: lat!,
          lng: lng!,
          accuracy: accuracy ?? undefined,
          remote: true,
          location_name: placeName.trim(),
          location_area: placeArea.trim() || FALLBACK_AREA,
          push_endpoint: push.endpoint
        });
        onSuccess(workLog, workLog.location_name || t('punchIn.fallbackField'));
      } else {
        workLog = await workLogsApi.punchIn({
          lat: lat!,
          lng: lng!,
          accuracy: accuracy ?? undefined,
          push_endpoint: push.endpoint
        });
        onSuccess(workLog, workLog.work_zone_name);
      }
      closeModal();
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      submitting = false;
    }
  }

  function closeModal() {
    closed = true;
    geoRequestID++;
    geocodeRequestID++;
    looking = false;
    onClose();
  }
</script>

<Modal title={t('punchIn.title')} onClose={closeModal}>
  <div class="space-y-5">
    {#if mode === 'home'}
      <div class="rounded-lg border border-toyama-teal/40 bg-toyama-teal/10 p-3.5">
        <p class="text-[13.5px] font-semibold text-navy">{t('punchIn.home.title')}</p>
        <p class="mt-1 text-[12.5px] leading-relaxed text-dark-gray">
          {t('punchIn.home.hint')}
        </p>
      </div>
    {/if}
    {#if mode === 'untagged'}
      <p class="text-[13px] leading-relaxed text-dark-gray">
        {t('punchIn.untagged')}
      </p>
    {/if}
    {#if true}
      <div>
        <div class="label">{t('common.location')}</div>
        {#if geoStatus === 'locating'}
          <p class="text-[13px] text-dark-gray">{t('punchIn.gettingLocation')}</p>
        {:else if geoStatus === 'ok'}
          {#if atSite && containingZone}
            <p class="text-[13px] text-toyama-teal">{t('punchIn.atSite', { name: containingZone.zone.name })}</p>
          {:else}
            <p class="text-[13px] text-dark-gray">
              {t('punchIn.locationCaptured', { lat: lat?.toFixed(5) ?? '', lng: lng?.toFixed(5) ?? '' })}
            </p>
          {/if}
        {:else}
          <div class="tym-error-state flex items-center justify-between gap-3">
            <span>{geoError}</span>
            <button type="button" class="btn-secondary shrink-0" onclick={() => void requestLocation()}>{t('common.retry')}</button>
          </div>
        {/if}

        {#if geoStatus === 'ok' && lat !== null && lng !== null}
          <div
            class="mt-2 h-[220px] w-full overflow-hidden rounded-md border border-gray-200"
            use:leafletMap={{
              points: [{ lat, lng, color: '#1d4ed8', tooltip: t('punchIn.you'), accuracyM: accuracy }],
              circles: (zonesQuery.data ?? []).map((z: WorkZone) => ({
                lat: z.latitude,
                lng: z.longitude,
                radiusM: z.radius,
                color: '#16a34a',
                tooltip: z.name
              }))
            }}
          ></div>
          <p class="mt-1.5 text-[11px] text-dark-gray">
            {lat.toFixed(5)}, {lng.toFixed(5)} &plusmn; {accuracy ? Math.round(accuracy) : '?'}m
            {#if nearestZone}
              {t('punchIn.kmTo', { km: (nearestZone.distance / 1000).toFixed(2), name: nearestZone.zone.name })}
            {/if}
          </p>
          {#if mode !== 'field' && !atSite && nearestZone}
            <p class="mt-1.5 text-[12px] leading-relaxed text-dark-gray">
              {t('punchIn.outsideSiteHint', { name: nearestZone.zone.name, distance: Math.round(nearestZone.distance), radius: nearestZone.zone.radius })}
            </p>
          {/if}
          <button type="button" class="btn-secondary mt-2" onclick={() => void requestLocation()}>
            {t('punchIn.refreshLocation')}
          </button>
        {/if}
      </div>

      {#if mode === 'field' && geoStatus === 'ok' && !atSite}
        <div>
          <div class="label">{t('punchIn.placeDetails')}</div>
          {#if looking}
            <p class="mb-1.5 text-[12px] text-dark-gray">{t('punchIn.lookingUp')}</p>
          {/if}
          {#if recents.length > 0}
            <div class="mb-2 flex flex-wrap gap-1.5">
              {#each recents as loc (loc.location_name + loc.maps_link)}
                <button
                  type="button"
                  class="tym-chip"
                  onclick={() => {
                    placeName = loc.location_name;
                    placeArea = loc.location_area;
                    nameEdited = true;
                    areaEdited = true;
                  }}
                >
                  <MapPin class="h-3 w-3" />
                  {loc.location_name}
                </button>
              {/each}
            </div>
          {/if}
          <input
            type="text"
            class="input"
            aria-label={t('punchIn.placeName')}
            placeholder={t('punchIn.placeName')}
            bind:value={placeName}
            oninput={() => (nameEdited = true)}
          />
          <input
            type="text"
            class="input mt-2"
            aria-label={t('punchIn.area')}
            placeholder={t('punchIn.area')}
            bind:value={placeArea}
            oninput={() => (areaEdited = true)}
          />
          <p class="mt-1.5 text-[12px] text-dark-gray">
            {t('punchIn.nameHint')}
          </p>
        </div>
      {/if}
    {/if}

    {#if error}
      <div class="tym-error-state">{error}</div>
    {/if}
  </div>
  {#snippet footer()}
    <div class="flex justify-end gap-2">
      <button type="button" class="btn-secondary" onclick={closeModal}>{t('common.cancel')}</button>
      <button type="button" class="btn-primary" disabled={!canSubmit} onclick={submit}>
        {submitting ? t('punchIn.punchingIn') : t('punchIn.title')}
      </button>
    </div>
  {/snippet}
</Modal>
